import React from "react";
import { Box, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import { Star } from "@mui/icons-material";

import { IRelease } from "../../interfaces/IRelease";
import { IChangeType } from "../../interfaces/IChangeType";
import { RELEASE_STAGES } from "../../interfaces/releaseStages";

export interface IProps {
    currentRelease: IRelease;
    changeTypeArray: IChangeType[];
}

/**
 * Vorschau des Changelogs, so wie er inhaltlich beim Kunden ankommt:
 * Kopf mit Version, Datum und Stufe, danach die Eintragungen nach Art
 * gruppiert — Highlights zuerst.
 */
export const ReleasePreview: React.FC<IProps> = ({ currentRelease, changeTypeArray }) => {
    const changes = currentRelease.changes ?? [];
    const highlights = changes.filter(x => x.isHighlighted);
    const rest = changes.filter(x => !x.isHighlighted);

    const typeName = (id: number) =>
        changeTypeArray.find(x => x.id === id)?.changeType ?? "Ohne Art";

    const stageName =
        RELEASE_STAGES.find(x => x.value === currentRelease.release_stage)?.title ??
        currentRelease.release_stage;

    const renderChange = (title: string, descriptionHtml: string, isHighlight: boolean) => (
        <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                {isHighlight && <Star fontSize="small" color="warning" />}
                <Typography variant="subtitle1" fontWeight={600}>
                    {title || "Ohne Titel"}
                </Typography>
            </Stack>
            <Box
                sx={{ "& p": { mt: 0.5, mb: 0.5 } }}
                dangerouslySetInnerHTML={{ __html: descriptionHtml || "" }}
            />
        </Box>
    );

    const groupedRest = changeTypeArray
        .map(type => ({
            type,
            items: rest.filter(x => x.ChangeType === type.id),
        }))
        .filter(group => group.items.length > 0);

    return (
        <Paper variant="outlined" sx={{ p: 2, position: "sticky", top: 8 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
                <Typography variant="h5">{currentRelease.title || "Ohne Version"}</Typography>
                <Chip size="small" label={stageName} />
                {currentRelease.is_disabled ? (
                    <Chip size="small" color="success" label="Freigegeben" />
                ) : (
                    <Chip size="small" color="default" label="Entwurf" />
                )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
                Veröffentlichung am{" "}
                {currentRelease.releaseAt
                    ? new Date(currentRelease.releaseAt).toLocaleDateString("de-DE")
                    : "—"}
            </Typography>

            {currentRelease.descriptionHtml && (
                <Box
                    sx={{ mt: 2 }}
                    dangerouslySetInnerHTML={{ __html: currentRelease.descriptionHtml }}
                />
            )}

            {highlights.length > 0 && (
                <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="overline" color="text.secondary">
                        Highlights
                    </Typography>
                    {highlights.map(change => (
                        <Box key={`preview-highlight-${change.id}`}>
                            {renderChange(change.title, change.descriptionHtml, true)}
                        </Box>
                    ))}
                </>
            )}

            {groupedRest.map(group => (
                <Box key={`preview-group-${group.type.id}`}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="overline" color="text.secondary">
                        {typeName(group.type.id)}
                    </Typography>
                    {group.items.map(change => (
                        <Box key={`preview-change-${change.id}`}>
                            {renderChange(change.title, change.descriptionHtml, false)}
                        </Box>
                    ))}
                </Box>
            ))}

            {changes.length === 0 && !currentRelease.descriptionHtml && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Noch kein Inhalt — die Vorschau füllt sich beim Schreiben.
                </Typography>
            )}
        </Paper>
    );
};
